/**
 * Permission decision cache (design §14.1/§14.4).
 *
 * It deliberately lives **outside** Redux: the shell store (`src/store/store.ts`)
 * is not editable by feature tasks, so a `permissions` slice is not available.
 * RTK Query is equally unsuitable as the cache (its entries expire by
 * `keepUnusedDataFor`, not by a per-decision TTL, and a mutation result is not
 * shareable across keys). A module store read through `useSyncExternalStore`
 * gives exactly the semantics §14 asks for: TTL per entry, in-flight dedupe,
 * prefix invalidation, and a reset when the session changes.
 *
 * FAIL CLOSED: a missing entry is `undefined` (render nothing / disable), an
 * evaluation error is stored as `allowed: false` with a short retry TTL. The
 * server stays the authority either way (P4).
 */

import { PERMISSION_RATE_LIMITED_TTL_MS, PERMISSION_TTL_MS } from './model';

export type PermissionKey = string; // `${action}::${resource}`

export interface PermissionEntry {
  allowed: boolean;
  evaluatedAt: number;
  /** How long this verdict may be reused (short for rate-limited/error denials). */
  ttlMs: number;
  /** True when the verdict came from a failed evaluation (network/403/500). */
  failed?: boolean;
}

export const permissionKey = (action: string, resource: string): PermissionKey =>
  `${action}::${resource}`;

let entries = new Map<PermissionKey, PermissionEntry>();
/** Shared so two components asking the same pair fire exactly one request (§14.3). */
let inflight = new Map<PermissionKey, Promise<PermissionEntry | undefined>>();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Fresh (TTL-respecting) cached verdict, or `undefined` when unresolved/stale.
 * Returned objects keep their identity until replaced, which makes this a valid
 * `useSyncExternalStore` snapshot.
 */
export function readPermission(key: PermissionKey): PermissionEntry | undefined {
  const entry = entries.get(key);
  if (!entry) {
    return undefined;
  }
  if (Date.now() - entry.evaluatedAt >= entry.ttlMs) {
    return undefined;
  }
  return entry;
}

export function writePermission(
  key: PermissionKey,
  allowed: boolean,
  options: { ttlMs?: number; failed?: boolean } = {},
): void {
  entries.set(key, {
    allowed,
    evaluatedAt: Date.now(),
    ttlMs: options.ttlMs ?? PERMISSION_TTL_MS,
    failed: options.failed,
  });
  emit();
}

/** Records a verdict returned by `POST /policies/evaluate/self`. */
export function writePermissionFromResult(
  key: PermissionKey,
  result: { allowed: boolean; rateLimit?: unknown },
): PermissionEntry {
  // A denial that carries `rateLimit` is transient, not a policy decision:
  // cache it for 10s only so the control comes back on its own (design §14.1).
  const transientDenial = result.allowed === false && result.rateLimit != null;
  const entry: PermissionEntry = {
    allowed: result.allowed,
    evaluatedAt: Date.now(),
    ttlMs: transientDenial ? PERMISSION_RATE_LIMITED_TTL_MS : PERMISSION_TTL_MS,
  };
  entries.set(key, entry);
  emit();
  return entry;
}

/** Marks one verdict stale, so the next check re-evaluates. */
export function expirePermission(key: PermissionKey): void {
  if (entries.delete(key)) {
    emit();
  }
}

/**
 * Drops every verdict whose resource matches exactly — e.g. `org:${orgId}`
 * after a member add/remove or a role assign/unassign/renew (§14.4).
 */
export function invalidatePermissionResource(resource: string): void {
  const suffix = `::${resource}`;
  let changed = false;
  entries.forEach((_entry, key) => {
    if (key.endsWith(suffix)) {
      entries.delete(key);
      changed = true;
    }
  });
  if (changed) {
    emit();
  }
}

/** Reset the whole cache whenever the session changes (§14.4). */
export function resetPermissionCache(): void {
  entries = new Map();
  inflight = new Map();
  emit();
}

/** Runs `task` once per key; concurrent callers share the same promise. */
export function beginInflight(
  key: PermissionKey,
  task: () => Promise<PermissionEntry | undefined>,
): Promise<PermissionEntry | undefined> {
  const existing = inflight.get(key);
  if (existing) {
    return existing;
  }

  const promise = task()
    .then((entry) => {
      inflight.delete(key);
      return entry;
    })
    .catch(() => {
      inflight.delete(key);
      // Fail closed, but retry soon: a transient outage must not lock the UI
      // out for the full TTL.
      writePermission(key, false, { ttlMs: PERMISSION_RATE_LIMITED_TTL_MS, failed: true });
      return entries.get(key);
    });

  inflight.set(key, promise);
  return promise;
}
